import { parseDays } from "../../lib/irrigation";
import { useEffect, useMemo, useState } from "react";
import { Droplets, Printer, BellRing } from "lucide-react";
import { toast } from "sonner";
import { buildIrrigationIcs, downloadIcs } from "../../lib/ics";
import { Reveal } from "../Reveal";
import { Moon } from "./JLunar";
import { COLS } from "./JSchedule";
import { JMixCalc } from "./JMixCalc";
import { JQRCard } from "./JQRCard";
import { JWaterCalendar } from "./JWaterCalendar";
import { PHASES, moonAge, phaseKey } from "../../lib/moon";
import { GENETICS } from "../../lib/plan";
import { scrollToId } from "../../lib/api";
import { readGrowWeek, saveGrowWeek } from "../../lib/growWeek";
import { buildWeeks, growQuery, toInput, useGrow } from "../../lib/irrigation";

const HINT = { nueva: "Descanso", creciente: "Esquejes", llena: "Cosecha", menguante: "Poda" };

// Table column (C1…F8) of the plan week that "today" falls in.
const colForToday = (weeks) => {
  const t = toInput(new Date());
  const i = weeks.findIndex((w) => w.days.some((d) => toInput(d.date) === t));
  if (i >= 0) return weeks[i].col;
  return new Date(weeks[0].days[0].date) > new Date() ? weeks[0].col : weeks[weeks.length - 1].col;
};

export const FOCUS_EVT = "jungle-focus-week";

// Fixed watering weekdays (0 = domingo). null = alternate days with Sunday rest.
const DOW = [[1, "L", "Lunes"], [2, "M", "Martes"], [3, "X", "Miércoles"], [4, "J", "Jueves"], [5, "V", "Viernes"], [6, "S", "Sábado"], [0, "D", "Domingo"]];

const chip = (on) => `h-10 min-w-10 px-3 rounded-full text-sm font-semibold border transition-colors ${on ? "bg-[#5B7FE0] border-[#8FB0FF] text-[#F6EEDC]" : "border-[#F6EEDC]/30 hover:border-[#F2B441] hover:text-[#F2B441]"}`;

const DayPicker = ({ days, setDays }) => {
  const toggle = (d) => {
    const cur = days ?? [];
    setDays(parseDays((cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]).join(",")));
  };
  return (
    <div data-testid="irrigation-days" className="mt-5">
      <p className="font-mono-d text-[11px] tracking-[0.2em] uppercase text-[#F2B441] mb-2">Días de riego</p>
      <div className="flex flex-wrap gap-1.5">
        <button onClick={() => setDays(null)} data-testid="irrigation-days-alternate" aria-pressed={!days} className={chip(!days)}>Alternos</button>
        {DOW.map(([d, s, name]) => (
          <button key={d} onClick={() => toggle(d)} aria-pressed={!!days?.includes(d)} aria-label={name} title={name}
            data-testid={`irrigation-day-${d}`} className={chip(!!days?.includes(d))}>{s}</button>
        ))}
      </div>
      <p data-testid="irrigation-days-summary" className="text-xs text-[#C9D6C2] mt-2">
        {days ? `Riegas: ${DOW.filter(([d]) => days.includes(d)).map(([, , n]) => n).join(", ")}.` : "Un día sí, otro no, con el domingo de descanso. Toca los días para fijarlos."}
      </p>
    </div>
  );
};

const Reminder = ({ weeks, start }) => {
  const [time, setTime] = useState("09:00");
  const save = () => {
    const n = weeks.reduce((a, w) => a + w.days.filter((d) => d.water).length, 0);
    downloadIcs(buildIrrigationIcs(weeks, time), `riegos-jungle-${start}.ics`);
    toast.success(`${n} riegos listos para tu calendario`, { description: "Abre el archivo descargado y pulsa Añadir todos." });
  };
  return (
    <div data-testid="irrigation-reminder" className="mt-5 rounded-[10px] border border-[#5B7FE0]/50 bg-[#1B3A8C]/40 p-4">
      <p className="font-mono-d text-[11px] tracking-[0.2em] uppercase text-[#8FB0FF] flex items-center gap-2"><BellRing className="w-3.5 h-3.5" /> Recordatorio en el móvil</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label htmlFor="irr-time" className="text-xs text-[#C9D6C2]">Aviso a las</label>
        <input id="irr-time" type="time" value={time} onChange={(e) => e.target.value && setTime(e.target.value)} data-testid="irrigation-reminder-time"
          className="h-10 rounded-full bg-[#0E2A1C] border border-[#F6EEDC]/30 px-3 text-sm text-[#F6EEDC] [color-scheme:dark] focus:border-[#F2B441] outline-none" />
        <button onClick={save} data-testid="irrigation-reminder-save"
          className="inline-flex items-center gap-2 h-10 rounded-full bg-[#F2B441] text-[#0E2A1C] px-4 text-sm font-bold hover:bg-[#F6EEDC] transition-colors">
          <BellRing className="w-4 h-4" /> Guardar en mi calendario
        </button>
      </div>
      <p className="text-[11px] text-[#C9D6C2] mt-2">Crea un evento con aviso cada día de riego, con las dosis. En iPhone se añade al abrir el archivo; en Android ábrelo con tu app de calendario (o impórtalo en Google Calendar desde el ordenador).</p>
    </div>
  );
};

export const JIrrigation = () => {
  const [cfg, update] = useGrow();
  const { start, days } = cfg;
  const setDays = (d) => update({ days: d });
  const weeks = useMemo(() => buildWeeks(cfg), [cfg]);
  const qrUrl = `${window.location.origin}/${growQuery(cfg)}#riego`;
  const [focus, setFocus] = useState(null);
  const [week, setWeekState] = useState(() => {
    const saved = readGrowWeek();
    const fromQr = new URLSearchParams(window.location.search).has("inicio");
    return !fromQr && COLS.includes(saved) ? saved : colForToday(weeks);
  });
  const setWeek = (w) => { setWeekState(w); saveGrowWeek(w); };
  const changeStart = (v) => { update({ start: v }); setWeek(colForToday(buildWeeks({ ...cfg, start: v }))); };
  useEffect(() => { saveGrowWeek(week); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (window.location.hash !== "#riego") return undefined;
    const t = setTimeout(() => scrollToId("riego"), 900);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    const on = (e) => {
      const w = weeks[e.detail - 1];
      if (!w) return;
      setFocus(w.n);
      setWeek(w.col);
      setTimeout(() => scrollToId(`riego-semana-${w.n}`), 50);
    };
    window.addEventListener(FOCUS_EVT, on);
    return () => window.removeEventListener(FOCUS_EVT, on);
  }, [weeks]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section id="riego" data-testid="irrigation-section" className="relative py-24 md:py-36 bg-[#143823] border-y border-[#F2B441]/25">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal className="mb-10 md:mb-14 grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <p className="font-mono-d text-[11px] tracking-[0.22em] uppercase text-[#8CD05A]">Cuadrante de riego</p>
            <h2 className="font-serif-d font-black text-5xl md:text-7xl leading-[0.92] tracking-tight mt-6">
              {days ? <>Tus días, <span className="italic font-semibold text-[#F2B441]">tu ritmo.</span></> : <>Un día sí, <span className="italic font-semibold text-[#F2B441]">otro no.</span></>}
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <p className="text-[#C9D6C2] leading-relaxed">Sigue tu plan de <b className="text-[#F6EEDC]">Mi cultivo</b> ({GENETICS[cfg.genetic].label}, {cfg.growth + cfg.flower} semanas): riego en días alternos con descanso el domingo, o en los días fijos que elijas, con nutrientes Jungle según la semana y la fase lunar de cada día.</p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <label className="font-mono-d text-[11px] tracking-[0.2em] uppercase text-[#F2B441]" htmlFor="irr-start">Inicio</label>
              <input id="irr-start" type="date" value={start} onChange={(e) => e.target.value && changeStart(e.target.value)} data-testid="irrigation-start-date"
                className="h-11 rounded-full bg-[#0E2A1C] border border-[#F6EEDC]/30 px-4 text-sm text-[#F6EEDC] [color-scheme:dark] focus:border-[#F2B441] outline-none" />
              <button onClick={() => window.print()} data-testid="irrigation-print" className="inline-flex items-center gap-2 h-11 rounded-full border border-[#F6EEDC]/30 px-4 text-sm hover:border-[#F2B441] hover:text-[#F2B441] transition-colors">
                <Printer className="w-4 h-4" /> Imprimir
              </button>
            </div>
            <DayPicker days={days} setDays={setDays} />
            <Reminder weeks={weeks} start={start} />
            <div className="mt-5">
              <JQRCard url={qrUrl} title="QR de tu tabla" testid="irrigation-qr" fileName={`jungle-tabla-riego-${start}`}
                caption="Escanéalo con el móvil para abrir este cuadrante con tu fecha de inicio y tus días de riego. Pégalo en el armario de cultivo." />
            </div>
          </div>
        </Reveal>

        <JMixCalc week={week} />
        <JWaterCalendar weeks={weeks} />
        <div data-testid="irrigation-grid" className="space-y-3">
          {weeks.map(({ n, col, label, days }, wi) => (
            <Reveal key={n} delay={Math.min(wi, 4) * 0.04}>
              <div id={`riego-semana-${n}`} data-testid={`irrigation-week-${n}`} className={`rounded-[10px] bg-[#0E2A1C] border p-3 md:p-4 scroll-mt-28 transition-colors ${focus === n ? "border-[#F2B441] ring-2 ring-[#F2B441]/40" : "border-[#F6EEDC]/10"}`}>
                <p className={`font-mono-d text-[11px] tracking-[0.2em] uppercase mb-3 ${col.startsWith("C") ? "text-[#8CD05A]" : "text-[#F2B441]"}`}>{label} <span className="text-[#C9D6C2]">· tabla {col}</span></p>
                <div data-lenis-prevent className="overflow-x-auto">
                  <div className="grid grid-cols-7 gap-2 min-w-[720px]">
                    {days.map(({ date, water, sunday, doses }) => {
                      const age = moonAge(date);
                      const pk = phaseKey(age);
                      return (
                      <div key={date.toISOString()} data-testid={`irrigation-day-${toInput(date)}`}
                        className={`rounded-[6px] p-2.5 min-h-[112px] border flex flex-col ${water ? "bg-[#1B3A8C]/60 border-[#5B7FE0]/50" : "border-dashed border-[#F6EEDC]/15"}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-mono-d text-[11px] text-[#C9D6C2] capitalize">{date.toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" })}</span>
                          {water && <Droplets className="w-3.5 h-3.5 text-[#8FB0FF]" />}
                        </div>
                        {water ? (
                          <ul className="mt-2 space-y-1">
                            {doses.length ? doses.map((x) => (
                              <li key={x.id} className="flex justify-between gap-1 text-[11px]"><span className="text-[#F6EEDC] truncate">{x.name}</span><span className="font-mono-d text-[#F2B441] whitespace-nowrap">{x.dose}</span></li>
                            )) : <li className="text-[11px] text-[#8FB0FF]">Solo agua (lavado)</li>}
                          </ul>
                        ) : <p className="mt-2 text-[11px] text-[#C9D6C2]">{sunday ? "Descanso (domingo)" : "Sin riego"}</p>}
                        <div data-testid={`irrigation-moon-${toInput(date)}`} title={PHASES[pk].name}
                          className="mt-auto pt-2 flex items-center gap-1.5 border-t border-[#F6EEDC]/10">
                          <Moon age={age} size={14} />
                          <span className="text-[11px]" style={{ color: PHASES[pk].tone }}>{HINT[pk]}</span>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="text-[#C9D6C2] text-xs mt-4">Dosis en mL por litro de agua (×100 = ml por 100 L), según el cuadrante oficial Jungle. Última semana: lavado con solo agua. Compact de F5 a F7 en cada riego.</p>
        <div className="flex flex-wrap gap-4 mt-3 font-mono-d text-[11px] text-[#C9D6C2]" data-testid="irrigation-moon-legend">
          <span>Luna del día:</span>
          {Object.entries(PHASES).map(([k, p]) => <span key={k} style={{ color: p.tone }}>{p.name} · {HINT[k]}</span>)}
        </div>
      </div>
    </section>
  );
};
